<main class="profile-page">
  <div
    class="profile-shell max-w-[1200px] mx-auto"
    *ngIf="authCredentials$ | async"
  >
    <ng-container *ngIf="profile$ | async as profile; else loadingProfile">
      <header class="page-heading mb-8">
        <div>
          <p class="eyebrow">Your learning space</p>
          <h1 class="font-bangers">My profile</h1>
          <p class="muted">
            Make it yours. Find everything you’ve shared with the community.
          </p>
        </div>
        <div class="actions flex flex-wrap items-center gap-3">
          <button
            type="button"
            class="profile-button primary"
            *ngIf="!isEditMode"
            (click)="enableEditMode()"
          >
            Edit profile</button
          ><button
            type="button"
            class="profile-button"
            (click)="signOut()"
            [disabled]="saving()"
          >
            Sign out
          </button>
        </div>
      </header>
      <div class="profile-layout">
        <aside
          class="profile-panel bg-white border border-slate-300 rounded-2xl p-5 sm:p-6 identity"
          aria-labelledby="identity-heading"
        >
          <div class="avatar-frame">
            <img
              [src]="'assets/images/avatars/' + currentAvatarImage"
              alt="Your superhero avatar"
              width="160"
              height="160"
            />
          </div>
          <p class="eyebrow">Study Hero</p>
          <h2 id="identity-heading" class="font-bangers">
            {{
              avatarData?.aliasName || profile.firstName || 'Your hero profile'
            }}
          </h2>
          <p *ngIf="profile.firstName || profile.lastName">
            {{ profile.firstName }} {{ profile.lastName }}
          </p>
          <p class="muted break-words">{{ profile.email }}</p>
          <button
            type="button"
            class="text-link"
            *ngIf="!avatarData?.aliasName && !isEditMode"
            (click)="enableEditMode()"
          >
            Choose your superhero name
          </button>
          <div
            class="badges flex flex-wrap items-center gap-3"
            *ngIf="profile.roles.length"
          >
            <span
              class="badge-label inline-block bg-blue-50 text-blue-800 rounded-md px-2.5 py-1 text-xs font-bold"
              *ngFor="let role of profile.roles"
              >{{ role.name }}</span
            >
          </div>
          <details
            class="hero-details mt-6 border-t border-slate-300 pt-5"
            *ngIf="avatarData?.superpowers?.primary || avatarData?.background"
          >
            <summary
              class="min-h-[44px] cursor-pointer font-semibold text-blue-800"
            >
              About your hero
            </summary>
            <dl>
              <ng-container *ngIf="avatarData?.superpowers?.primary"
                ><dt class="text-slate-600 text-xs mt-3">Primary power</dt>
                <dd class="capitalize break-words">
                  {{ displayLabel(avatarData?.superpowers?.primary) }}
                </dd></ng-container
              >
              <ng-container *ngIf="avatarData?.superpowers?.secondary"
                ><dt class="text-slate-600 text-xs mt-3">Secondary power</dt>
                <dd class="capitalize break-words">
                  {{ displayLabel(avatarData?.superpowers?.secondary) }}
                </dd></ng-container
              >
              <ng-container *ngIf="avatarData?.superpowers?.weakness"
                ><dt class="text-slate-600 text-xs mt-3">Weakness</dt>
                <dd class="capitalize break-words">
                  {{ displayLabel(avatarData?.superpowers?.weakness) }}
                </dd></ng-container
              >
              <ng-container *ngIf="avatarData?.background?.origin"
                ><dt class="text-slate-600 text-xs mt-3">Origin</dt>
                <dd class="capitalize break-words">
                  {{ displayLabel(avatarData?.background?.origin) }}
                </dd></ng-container
              >
              <ng-container *ngIf="avatarData?.background?.motivation"
                ><dt class="text-slate-600 text-xs mt-3">Motivation</dt>
                <dd class="capitalize break-words">
                  {{ displayLabel(avatarData?.background?.motivation) }}
                </dd></ng-container
              >
            </dl>
            <p class="muted story" *ngIf="avatarData?.background?.backstory">
              {{ avatarData?.background?.backstory }}
            </p>
          </details>
        </aside>
        <div class="profile-content grid gap-6 min-w-0">
          <section
            *ngIf="isEditMode"
            class="profile-panel bg-white border border-slate-300 rounded-2xl p-5 sm:p-6"
            aria-labelledby="edit-heading"
          >
            <div class="section-heading mb-6">
              <div>
                <h2 id="edit-heading" class="font-bangers">Edit profile</h2>
                <p class="muted">Update your details and choose your hero.</p>
              </div>
            </div>
            <form [formGroup]="profileForm" (ngSubmit)="saveProfile()">
              <fieldset
                [disabled]="saving()"
                class="edit-fields grid gap-5 min-w-0"
              >
                <legend class="sr-only">Profile details</legend>
                <div class="field-grid grid sm:grid-cols-2 gap-4">
                  <div class="field">
                    <label for="firstName"
                      >First name <span class="muted">(optional)</span></label
                    ><input
                      id="firstName"
                      autocomplete="given-name"
                      formControlName="firstName"
                    />
                  </div>
                  <div class="field">
                    <label for="lastName"
                      >Last name <span class="muted">(optional)</span></label
                    ><input
                      id="lastName"
                      autocomplete="family-name"
                      formControlName="lastName"
                    />
                  </div>
                </div>
                <div class="field">
                  <label for="profileEmail">Email</label
                  ><input
                    id="profileEmail"
                    type="email"
                    autocomplete="email"
                    formControlName="email"
                    aria-describedby="email-error"
                    [attr.aria-invalid]="
                      profileForm.controls.email.touched &&
                      profileForm.controls.email.invalid
                    "
                  />
                  <p
                    id="email-error"
                    class="field-error text-red-800 text-sm mt-1.5"
                    *ngIf="
                      profileForm.controls.email.touched &&
                      profileForm.controls.email.invalid
                    "
                  >
                    Enter a valid email address.
                  </p>
                </div>
                <div class="field">
                  <label for="aliasName">Superhero name</label>
                  <div class="alias-input flex flex-col sm:flex-row gap-3">
                    <input
                      id="aliasName"
                      formControlName="aliasName"
                      aria-describedby="alias-error"
                      [attr.aria-invalid]="
                        profileForm.controls.aliasName.touched &&
                        profileForm.controls.aliasName.invalid
                      "
                    /><button
                      type="button"
                      class="profile-button"
                      (click)="generateNewAlias()"
                    >
                      Random name
                    </button>
                  </div>
                  <p
                    id="alias-error"
                    class="field-error text-red-800 text-sm mt-1.5"
                    *ngIf="
                      profileForm.controls.aliasName.touched &&
                      profileForm.controls.aliasName.invalid
                    "
                  >
                    Choose a superhero name.
                  </p>
                </div>
                <fieldset class="choice-field">
                  <legend>Hero avatar</legend>
                  <div
                    class="avatar-options grid grid-cols-[repeat(auto-fill,minmax(80px,1fr))] gap-3"
                  >
                    <label
                      *ngFor="let avatar of availableAvatars; let i = index"
                      class="avatar-option relative"
                      ><input
                        type="radio"
                        formControlName="avatarImage"
                        [value]="avatar"
                        [attr.aria-label]="'Hero avatar ' + (i + 1)" /><img
                        [src]="'assets/images/avatars/' + avatar"
                        alt=""
                        width="80"
                        height="80"
                        loading="lazy"
                    /></label>
                  </div>
                </fieldset>
                <div class="field-grid grid sm:grid-cols-2 gap-4">
                  <div class="field">
                    <label for="primaryPower">Primary power</label
                    ><select id="primaryPower" formControlName="primaryPower">
                      <option
                        *ngFor="let power of predefinedPowers"
                        [value]="power.value"
                      >
                        {{ power.label }}
                      </option>
                    </select>
                  </div>
                  <div class="field">
                    <label for="weakness">Weakness</label
                    ><select id="weakness" formControlName="weakness">
                      <option value="">None selected</option>
                      <option
                        *ngFor="let weakness of predefinedWeaknesses"
                        [value]="weakness.value"
                      >
                        {{ weakness.label }}
                      </option>
                    </select>
                  </div>
                </div>
              </fieldset>
              <div class="actions flex flex-wrap items-center gap-3 mt-6">
                <button
                  type="submit"
                  class="profile-button primary"
                  [disabled]="saving()"
                >
                  {{ saving() ? 'Saving…' : 'Save changes' }}</button
                ><button
                  type="button"
                  class="profile-button"
                  (click)="cancelEdit()"
                  [disabled]="saving()"
                >
                  Cancel
                </button>
              </div>
            </form>
          </section>
          <section
            class="profile-panel bg-white border border-slate-300 rounded-2xl p-5 sm:p-6 activity"
            aria-labelledby="activity-heading"
            *ngIf="resources$ | async as resources"
          >
            <ng-container *ngIf="contributions$ | async as contributions">
              <div class="section-heading mb-6">
                <div>
                  <p class="eyebrow">Shared by you</p>
                  <h2 id="activity-heading" class="font-bangers">
                    My activity
                  </h2>
                  <p class="muted">
                    Your resources and the work you’re credited for.
                  </p>
                </div>
                <a class="profile-button primary" routerLink="/resources/create"
                  >Add a resource</a
                >
              </div>
              <nav
                class="activity-switch flex flex-wrap gap-2 border-b border-slate-300 pb-3"
                aria-label="Choose your activity"
              >
                <button
                  type="button"
                  [class.selected]="activeActivity === 'resources'"
                  [attr.aria-pressed]="activeActivity === 'resources'"
                  (click)="activeActivity = 'resources'"
                  aria-controls="resources-panel"
                >
                  My resources
                  <span *ngIf="!resources.loading && !resources.error">{{
                    resources.total
                  }}</span>
                </button>
                <button
                  type="button"
                  [class.selected]="activeActivity === 'contributions'"
                  [attr.aria-pressed]="activeActivity === 'contributions'"
                  (click)="activeActivity = 'contributions'"
                  aria-controls="contributions-panel"
                >
                  My contributions
                  <span
                    *ngIf="!contributions.loading && !contributions.error"
                    >{{ contributions.total }}</span
                  >
                </button>
              </nav>
              <div
                id="resources-panel"
                *ngIf="activeActivity === 'resources'"
                [attr.aria-busy]="resources.loading"
              >
                <p class="activity-description my-5 text-sm muted">
                  Learning resources you’ve created, including your remixes.
                </p>
                <div
                  class="empty-state text-center py-10 px-4 bg-slate-50 rounded-xl"
                  *ngIf="resources.loading"
                  role="status"
                >
                  Loading your resources…
                </div>
                <div
                  class="empty-state text-center py-10 px-4 bg-slate-50 rounded-xl"
                  *ngIf="resources.error"
                  role="alert"
                >
                  <h3>We couldn’t load your resources</h3>
                  <p>Please try again.</p>
                  <button
                    type="button"
                    class="profile-button"
                    (click)="resourcePage$.next(resources.page)"
                  >
                    Try again
                  </button>
                </div>
                <div
                  class="empty-state text-center py-10 px-4 bg-slate-50 rounded-xl"
                  *ngIf="
                    !resources.loading &&
                    !resources.error &&
                    !resources.items.length
                  "
                >
                  <h3>Your first resource starts here</h3>
                  <p>
                    Share a lesson, video or study material. It will appear here
                    when you create it.
                  </p>
                  <a
                    class="profile-button primary"
                    routerLink="/resources/create"
                    >Create a resource</a
                  >
                </div>
                <div
                  class="activity-list"
                  *ngIf="!resources.loading && !resources.error"
                >
                  <article
                    class="activity-item py-5 border-b border-slate-300"
                    *ngFor="let resource of resources.items"
                  >
                    <div class="item-copy min-w-0 break-words">
                      <span
                        class="badge-label inline-block bg-blue-50 text-blue-800 rounded-md px-2.5 py-1 text-xs font-bold"
                        >{{ resource.resourceType.name || 'Resource' }}</span
                      >
                      <h3>
                        <a [routerLink]="['/resources', resource.id]">{{
                          resource.name
                        }}</a>
                      </h3>
                      <p
                        class="muted item-description"
                        *ngIf="resource.description"
                      >
                        {{ resource.description }}
                      </p>
                    </div>
                    <div
                      class="item-actions flex flex-wrap items-center gap-3 shrink-0"
                    >
                      <a
                        class="text-link"
                        [routerLink]="['/resources', resource.id]"
                        [attr.aria-label]="'View ' + resource.name"
                        >View</a
                      ><a
                        class="profile-button"
                        [routerLink]="['/resources', resource.id, 'edit']"
                        [attr.aria-label]="'Edit ' + resource.name"
                        >Edit</a
                      >
                    </div>
                  </article>
                </div>
                <nav
                  class="pagination flex flex-wrap items-center justify-between gap-3 mt-6 text-sm"
                  *ngIf="
                    !resources.loading &&
                    !resources.error &&
                    resources.totalPages > 1
                  "
                  aria-label="Resource pages"
                >
                  <button
                    type="button"
                    class="profile-button"
                    [disabled]="resources.page <= 1"
                    (click)="resourcePage$.next(resources.page - 1)"
                  >
                    Previous</button
                  ><span
                    >Page {{ resources.page }} of
                    {{ resources.totalPages }}</span
                  ><button
                    type="button"
                    class="profile-button"
                    [disabled]="resources.page >= resources.totalPages"
                    (click)="resourcePage$.next(resources.page + 1)"
                  >
                    Next
                  </button>
                </nav>
              </div>
              <div
                id="contributions-panel"
                *ngIf="activeActivity === 'contributions'"
                [attr.aria-busy]="contributions.loading"
              >
                <p class="activity-description my-5 text-sm muted">
                  Credits linked to your account for resources, books and
                  editions.
                </p>
                <div
                  class="empty-state text-center py-10 px-4 bg-slate-50 rounded-xl"
                  *ngIf="contributions.loading"
                  role="status"
                >
                  Loading your contributions…
                </div>
                <div
                  class="empty-state text-center py-10 px-4 bg-slate-50 rounded-xl"
                  *ngIf="contributions.error"
                  role="alert"
                >
                  <h3>We couldn’t load your contributions</h3>
                  <p>Please try again.</p>
                  <button
                    type="button"
                    class="profile-button"
                    (click)="contributionPage$.next(contributions.page)"
                  >
                    Try again
                  </button>
                </div>
                <div
                  class="empty-state text-center py-10 px-4 bg-slate-50 rounded-xl"
                  *ngIf="
                    !contributions.loading &&
                    !contributions.error &&
                    !contributions.items.length
                  "
                >
                  <h3>No contribution credits yet</h3>
                  <p>
                    When you’re credited as a contributor, your work will appear
                    here.
                  </p>
                  <a class="profile-button" routerLink="/get-involved"
                    >Explore ways to contribute</a
                  >
                </div>
                <div
                  class="activity-list"
                  *ngIf="!contributions.loading && !contributions.error"
                >
                  <article
                    class="activity-item py-5 border-b border-slate-300"
                    *ngFor="let contribution of contributions.items"
                  >
                    <div class="item-copy min-w-0 break-words">
                      <div class="badges flex flex-wrap items-center gap-3">
                        <span
                          class="badge-label inline-block bg-blue-50 text-blue-800 rounded-md px-2.5 py-1 text-xs font-bold"
                          >{{ contribution.roleName }}</span
                        ><span class="muted">{{
                          contribution.content.type === 'BookEdition'
                            ? 'Book edition'
                            : contribution.content.type
                        }}</span>
                      </div>
                      <h3>
                        <a
                          *ngIf="
                            contributionLink(contribution) as link;
                            else contributionTitle
                          "
                          [routerLink]="link"
                          >{{ contribution.content.title }}</a
                        ><ng-template #contributionTitle>{{
                          contribution.content.title
                        }}</ng-template>
                      </h3>
                      <p class="muted">
                        Credited
                        {{ contribution.createdAt | date: 'mediumDate' }}
                      </p>
                    </div>
                    <a
                      *ngIf="contributionLink(contribution) as link"
                      class="profile-button"
                      [routerLink]="link"
                      [attr.aria-label]="'View ' + contribution.content.title"
                      >View work</a
                    >
                  </article>
                </div>
                <nav
                  class="pagination flex flex-wrap items-center justify-between gap-3 mt-6 text-sm"
                  *ngIf="
                    !contributions.loading &&
                    !contributions.error &&
                    contributions.totalPages > 1
                  "
                  aria-label="Contribution pages"
                >
                  <button
                    type="button"
                    class="profile-button"
                    [disabled]="contributions.page <= 1"
                    (click)="contributionPage$.next(contributions.page - 1)"
                  >
                    Previous</button
                  ><span
                    >Page {{ contributions.page }} of
                    {{ contributions.totalPages }}</span
                  ><button
                    type="button"
                    class="profile-button"
                    [disabled]="contributions.page >= contributions.totalPages"
                    (click)="contributionPage$.next(contributions.page + 1)"
                  >
                    Next
                  </button>
                </nav>
              </div>
            </ng-container>
          </section>
          <section
            class="profile-panel bg-white border border-slate-300 rounded-2xl p-5 sm:p-6 account-settings"
            aria-labelledby="account-heading"
          >
            <h2 id="account-heading" class="text-lg font-bold">
              Account settings
            </h2>
            <p class="muted mt-2 mb-4">
              Deleting your account permanently removes your profile and
              personal data. Your contributions remain in the community as
              “Deleted User”.
            </p>
            <button
              type="button"
              class="profile-button danger"
              (click)="openDeleteModal()"
              [disabled]="saving()"
            >
              Delete my account
            </button>
          </section>
        </div>
      </div>
    </ng-container>
    <ng-template #loadingProfile
      ><div
        class="profile-panel bg-white border border-slate-300 rounded-2xl p-5 sm:p-6 empty-state"
        role="status"
      >
        Loading your profile…
      </div></ng-template
    >
  </div>
</main>
<dialog
  #deleteAccountModal
  class="modal"
  aria-labelledby="delete-heading"
  (cancel)="deleting() && $event.preventDefault()"
>
  <div class="modal-box bg-white text-slate-900">
    <h2 id="delete-heading" class="font-bold text-2xl text-red-800">
      Delete your account?
    </h2>
    <p class="py-3">This is permanent and cannot be undone. We will:</p>
    <ul class="list-disc list-inside space-y-1 text-sm text-slate-700">
      <li>Delete your profile and personal data.</li>
      <li>Sign you out and delete your login.</li>
      <li>
        Keep your contributions in the community, shown as “Deleted User”.
      </li>
    </ul>
    <div class="form-control mt-4">
      <label class="label" for="deleteConfirm"
        ><span class="text-slate-900"
          >Type <strong>{{ confirmWord }}</strong> to confirm.</span
        ></label
      ><input
        type="text"
        id="deleteConfirm"
        name="deleteConfirm"
        [(ngModel)]="confirmText"
        [disabled]="deleting()"
        autocomplete="off"
        [placeholder]="confirmWord"
        class="input input-bordered w-full bg-white text-slate-900"
      />
    </div>
    <div class="modal-action">
      <button
        type="button"
        class="profile-button"
        (click)="closeDeleteModal()"
        [disabled]="deleting()"
      >
        Cancel</button
      ><button
        type="button"
        class="profile-button danger"
        (click)="onConfirmDelete()"
        [disabled]="confirmText !== confirmWord || deleting()"
      >
        {{ deleting() ? 'Deleting…' : 'Delete account' }}
      </button>
    </div>
  </div>
</dialog>
